/**
 * api/email.js — SMTP settings, templates, outbox, FastAPI helpers.
 */
import { supabase } from "@/lib/supabaseClient";
import { backendCall } from "./_shared";

export const EMAIL_TEMPLATE_KEYS = [
  "booking_created", "booking_approved", "booking_rejected",
  "booking_reminder", "driver_assigned", "checkin_confirmation",
  "booking_completed", "booking_updated", "booking_cancelled", "waitlist_offer", "pallet_balance_digest",
];

/* Templates ------------------------------------------------------------ */

export async function listEmailTemplates({ clientId = null } = {}) {
  let q = supabase
    .from("lbs_email_templates")
    .select("id, client_id, template_key, subject, body_html, body_text, updated_at")
    .order("template_key", { ascending: true });
  if (clientId) q = q.or(`client_id.is.null,client_id.eq.${clientId}`);
  else          q = q.is("client_id", null);
  return q;
}

export async function upsertEmailTemplate(values) {
  if (values.id) {
    return supabase
      .from("lbs_email_templates")
      .update({
        subject:   values.subject,
        body_html: values.body_html,
        body_text: values.body_text,
        updated_at: new Date().toISOString(),
      })
      .eq("id", values.id)
      .select()
      .maybeSingle();
  }
  return supabase
    .from("lbs_email_templates")
    .insert({
      client_id:    values.client_id,
      template_key: values.template_key,
      subject:      values.subject,
      body_html:    values.body_html,
      body_text:    values.body_text,
    })
    .select()
    .maybeSingle();
}

export async function deleteEmailTemplate(id) {
  return supabase.from("lbs_email_templates").delete().eq("id", id).select();
}

/* Settings ------------------------------------------------------------- */

export async function getEmailSettings(clientId) {
  return supabase
    .from("lbs_client_email_settings")
    .select("*")
    .eq("client_id", clientId)
    .maybeSingle();
}

export async function upsertEmailSettings(values) {
  return supabase
    .from("lbs_client_email_settings")
    .upsert(values, { onConflict: "client_id" })
    .select()
    .maybeSingle();
}

/* Backend operations --------------------------------------------------- */

export const setSmtpPassword  = (clientId, password) =>
  backendCall("/api/email/set-password", { client_id: clientId, smtp_password: password });
export const sendTestEmail    = (clientId, toEmail) =>
  backendCall("/api/email/test", { client_id: clientId, to_email: toEmail });
export const sendBroadcastTest = (payload) => backendCall("/api/broadcasts/test", payload);
export const processOutboxNow = (limit = 25) =>
  backendCall("/api/email/process-outbox", { limit });

export const autoArchiveNow = () => backendCall("/api/admin/auto-archive-now", {});

/* Outbox view ---------------------------------------------------------- */

export async function listEmailOutbox({ status = null, limit = 50 } = {}) {
  let q = supabase
    .from("lbs_email_outbox")
    .select("id,client_id,template_key,to_email,subject,body_html,body_text,status,attempts,last_error,created_at,sent_at,entity_type,entity_id,context")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (status) q = q.eq("status", status);
  return q;
}

/**
 * Reset a failed outbox row back to 'pending' so the drainer picks it
 * up on the next tick. Client-side call is enough — RLS + row status
 * check on the DB side prevent replaying sent rows.
 */
export async function retryEmailOutboxRow(id) {
  return supabase
    .from("lbs_email_outbox")
    .update({ status: "pending", last_error: null })
    .eq("id", id)
    .in("status", ["failed", "skipped"])
    .select()
    .maybeSingle();
}
